import { useEffect, useMemo, useRef, useState } from 'react';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import StreamedOutputReadonly from '../../../components/StreamedOutputReadonly';
import { ENTITY_NAME_CONTENT, ENTITY_NAME_BACK_OFFICE_PROMPT_TEMPLATE, OPERATOR_AND, OPERATOR_LIKE_CASEINSENSITIVE } from '../../../dao/dbConstants';
import { searchEntityRecords } from '../../../utils/apiUtils';
import { showErrorMessage } from '@/shared/components/toast-system';
import { SERVER_URL } from '../../../utils/apiUtilsConstants';
import { extractVariablesFromTemplate, applyVariablesToTemplate, DEFAULT_PLAYGROUND_VALUES } from './promptTemplateUtils';

const PromptTemplatePlayground = ({
  record,
  templatesByType,
  llmProviders,
  llmModels,
  selectedProvider,
  selectedModel,
  llmTemperature,
  llmMaxTokens,
  onProviderChange,
  onModelChange,
  onTemperatureChange,
  onMaxTokensChange,
}) => {
  const [defaultPlaygroundValues, setDefaultPlaygroundValues] = useState(DEFAULT_PLAYGROUND_VALUES);
  const [playgroundValues, setPlaygroundValues] = useState(DEFAULT_PLAYGROUND_VALUES);
  const [isPlaygroundGenerating, setIsPlaygroundGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageError, setImageError] = useState(null);
  const streamedOutputRef = useRef(null);
  const playgroundStorageKey = useMemo(() => {
    if (!record) return null;
    if (record.id) return `promptPlaygroundValues:id:${record.id}`;
    return `promptPlaygroundValues:name:${record.category || ''}:${record.name || ''}`;
  }, [record]);

  const selectedModelInfo = useMemo(() => {
    return llmModels.find(m => m.id === selectedModel) || null;
  }, [llmModels, selectedModel]);

  const isImageModel = selectedModelInfo?.type === 'image';

  const variablesInTemplate = useMemo(() => {
    const allText = [
      templatesByType.system.template,
      templatesByType.user.template,
      templatesByType.assistant.template,
    ]
      .filter(Boolean)
      .join('\n');
    return extractVariablesFromTemplate(allText);
  }, [templatesByType]);

  useEffect(() => {
    setPlaygroundValues((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.entries(defaultPlaygroundValues).forEach(([key, value]) => {
        if (variablesInTemplate.includes(key) && (!next[key] || !next[key].trim())) {
          next[key] = value;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [variablesInTemplate, defaultPlaygroundValues]);

  useEffect(() => {
    let isMounted = true;
    const loadSeoGuidelinesDefault = async () => {
      if (!variablesInTemplate.includes('seoGuidelines')) return;
      try {
        const group = {
          operator: OPERATOR_AND,
          groups: [
            {
              operator: OPERATOR_AND,
              criteria: [
                { fieldName: 'category', operator: OPERATOR_LIKE_CASEINSENSITIVE, value: 'internal' },
                { fieldName: 'name', operator: OPERATOR_LIKE_CASEINSENSITIVE, value: 'seo guidelines system' },
                { fieldName: 'type', operator: OPERATOR_LIKE_CASEINSENSITIVE, value: 'system' },
              ],
            },
          ],
        };

        const result = await searchEntityRecords(
          ENTITY_NAME_BACK_OFFICE_PROMPT_TEMPLATE,
          { pageNumber: 1, pageSize: 1, group }
        );
        const template = result?.data?.list?.[0]?.template ?? '';
        if (isMounted && template) {
          setDefaultPlaygroundValues((prev) => ({
            ...prev,
            seoGuidelines: template,
          }));
        }
      } catch (error) {
        console.warn('PromptTemplatePlayground: Failed to load SEO Guidelines System template.', error);
      }
    };

    loadSeoGuidelinesDefault();
    return () => {
      isMounted = false;
    };
  }, [variablesInTemplate]);

  useEffect(() => {
    if (!playgroundStorageKey) return;
    try {
      const raw = sessionStorage.getItem(playgroundStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        setPlaygroundValues((prev) => ({ ...prev, ...parsed }));
      }
    } catch (error) {
      console.warn('PromptTemplatePlayground: Failed to restore playground values.', error);
    }
  }, [playgroundStorageKey]);

  useEffect(() => {
    if (!playgroundStorageKey) return;
    try {
      sessionStorage.setItem(playgroundStorageKey, JSON.stringify(playgroundValues));
    } catch (error) {
      console.warn('PromptTemplatePlayground: Failed to persist playground values.', error);
    }
  }, [playgroundStorageKey, playgroundValues]);

  const renderedTemplate = useMemo(() => {
    const parts = [];
    const systemResolved = applyVariablesToTemplate(templatesByType.system.template, playgroundValues);
    const userResolved = applyVariablesToTemplate(templatesByType.user.template, playgroundValues);
    const assistantResolved = applyVariablesToTemplate(templatesByType.assistant.template, playgroundValues);

    if (systemResolved) {
      parts.push('[system]\n' + systemResolved);
    }
    if (userResolved) {
      parts.push('[user]\n' + userResolved);
    }
    if (assistantResolved) {
      parts.push('[assistant]\n' + assistantResolved);
    }

    return parts.join('\n\n');
  }, [templatesByType, playgroundValues]);

  const onPlaygroundGenerate = async () => {
    if (isImageModel) {
      const userResolved = applyVariablesToTemplate(templatesByType.user.template, playgroundValues);
      if (!userResolved || !userResolved.trim()) {
        showErrorMessage('User prompt is required for image generation.');
        return;
      }

      setIsPlaygroundGenerating(true);
      setImageError(null);
      setGeneratedImage(null);

      try {
        const response = await fetch(`${SERVER_URL}/api/ui/bo/prompts/playground`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            prompts: { user: userResolved },
            modelParams: {
              modelName: selectedModel || undefined,
              size: '1024x1024',
              quality: 'standard',
            },
            isImageGeneration: true,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to generate image');
        }

        const data = await response.json();
        if (data.success && data.imageBase64String) {
          setGeneratedImage(`data:image/png;base64,${data.imageBase64String}`);
        } else {
          throw new Error('No image data received');
        }
      } catch (error) {
        setImageError(error.message || 'Failed to generate image');
        showErrorMessage(error.message || 'Failed to generate image');
      } finally {
        setIsPlaygroundGenerating(false);
      }
      return;
    }

    if (!templatesByType.system.template || !templatesByType.system.template.trim()) {
      showErrorMessage('System Prompt is required before generating.');
      return;
    }
    if (!streamedOutputRef.current || typeof streamedOutputRef.current.generateContent !== 'function') {
      showErrorMessage('Playground output is not ready.');
      return;
    }

    const prompts = {};
    const systemResolved = applyVariablesToTemplate(templatesByType.system.template, playgroundValues);
    const userResolved = applyVariablesToTemplate(templatesByType.user.template, playgroundValues);
    const assistantResolved = applyVariablesToTemplate(templatesByType.assistant.template, playgroundValues);
    if (systemResolved) prompts.system = systemResolved;
    if (userResolved) prompts.user = userResolved;
    if (assistantResolved) prompts.assistant = assistantResolved;

    const input = {
      screenName: 'Back Office > Prompt Template Playground',
      entityName: ENTITY_NAME_CONTENT,
      id: 0,
      category: record?.category,
      name: record?.name,
      prompts,
      modelParams: {
        provider: selectedProvider || undefined,
        modelName: selectedModel || undefined,
        temperature: llmTemperature,
        maxOutputTokens: llmMaxTokens,
      },
    };

    try {
      setIsPlaygroundGenerating(true);
      streamedOutputRef.current.generateContent(input);
    } catch (error) {
      setIsPlaygroundGenerating(false);
      showErrorMessage(error?.message || 'Failed to generate preview from AI.');
    }
  };

  const onPlaygroundStreamingChange = (flag) => {
    setIsPlaygroundGenerating(flag);
  };

  const onPlaygroundGenerationError = (error) => {
    setIsPlaygroundGenerating(false);
    const message = error?.message || 'Failed to generate preview from AI.';
    showErrorMessage(message);
  };

  const generatePromptTemplatePlayground = async (input) => {
    try {
      return fetch(`${SERVER_URL}/api/ui/bo/prompts/playground`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(input ?? {}),
      });
    } catch (error) {
      console.error('PromptTemplate.playground - Error calling backend:', error);
      throw error;
    }
  };

  return (
    <div
      className="record-form-fields-container"
      style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
    >
      <div style={{ padding: '16px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
        <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>LLM Configuration</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div className="input-control separate-label">
            <label>Provider</label>
            <select
              value={selectedProvider}
              onChange={(e) => onProviderChange(e.target.value)}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            >
              <option value="">Select a provider...</option>
              {llmProviders.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
          </div>

          <div className="input-control separate-label">
            <label>Model</label>
            <select
              value={selectedModel}
              onChange={(e) => onModelChange(e.target.value)}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            >
              <option value="">Select a model...</option>
              {llmModels
                .filter((m) => !selectedProvider || m.providerId === selectedProvider)
                .map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} {model.maxInputTokens ? `(${Math.round(model.maxInputTokens / 1000)}K context)` : ''}
                  </option>
                ))}
            </select>
          </div>

          <div className="input-control separate-label">
            <label>Temperature</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={llmTemperature}
                onChange={(e) => onTemperatureChange(parseFloat(e.target.value))}
                style={{ flex: 1 }}
              />
              <span style={{ minWidth: '40px', textAlign: 'right' }}>{llmTemperature}</span>
            </div>
          </div>

          <div className="input-control separate-label">
            <label>Max Tokens</label>
            <input
              type="number"
              value={llmMaxTokens}
              onChange={(e) => onMaxTokensChange(parseInt(e.target.value) || 4000)}
              min={100}
              max={128000}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            />
          </div>
        </div>
      </div>

      {}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {}
        <div className='playground-variables' style={{ flex: 1 }}>
          <div className="input-control separate-label">
            <label>Playground Variables (from available variables)</label>
            {variablesInTemplate.length === 0 && (
              <div className="input-help-text">
                No <code>{'{{variables}}'}</code> detected in this template. The full prompt is shown below.
              </div>
            )}
            {variablesInTemplate.map((v) => (
              <div key={v} className="playground-variable-row" style={{ marginBottom: '16px' }}>
                <div className="playground-variable-label" style={{ marginBottom: '4px' }}>
                  <code>{`{{${v}}}`}</code>
                </div>
                <Input
                  type="textarea"
                  label={null}
                  name={`playground_${v}`}
                  value={playgroundValues[v] ?? ''}
                  onChange={(val) =>
                    setPlaygroundValues((prev) => ({ ...prev, [v]: val }))
                  }
                  style={{ width: '100%', minHeight: '80px' }}
                />
              </div>
            ))}
          </div>

          <div className="input-control separate-label">
            <label>Rendered Prompt Preview (all roles)</label>
            <textarea
              readOnly
              value={renderedTemplate}
              style={{
                width: '100%',
                minHeight: '260px',
                padding: '8px',
                lineHeight: '1.6',
                fontSize: '13px',
              }}
            />
            <div className="input-help-text">
              Variables are replaced with the sample values above across system, user, and assistant prompts. This approximates the full prompt context sent to the model.
            </div>
          </div>

          <div className="input-control separate-label" style={{ textAlign: 'right' }}>
            <Button
              className="primary"
              onClick={onPlaygroundGenerate}
              disabled={
                isPlaygroundGenerating ||
                (isImageModel
                  ? !templatesByType.user.template?.trim()
                  : !templatesByType.system.template?.trim())
              }
            >
              {isPlaygroundGenerating ? 'Generating…' : 'Generate'}
            </Button>
          </div>
        </div>

        {}
        <div style={{ flex: 1 }}>
          {isImageModel ? (
            <>
              <div className="input-control separate-label">
                <label>Generated Image</label>
                <div className="input-help-text">
                  Image generated using {selectedModelInfo?.name || selectedModel}
                </div>
              </div>
              <div className="input-control separate-label">
                {isPlaygroundGenerating ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    Generating image...
                  </div>
                ) : imageError ? (
                  <div style={{ padding: '20px', backgroundColor: '#fee', borderRadius: '8px', color: '#c00' }}>
                    Error: {imageError}
                  </div>
                ) : generatedImage ? (
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={generatedImage}
                      alt="Generated"
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#999', border: '2px dashed #ddd', borderRadius: '8px' }}>
                    Click "Generate" to create an image
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="input-control separate-label">
                <label>LLM Response</label>
                <div className="input-help-text">
                  Streaming response using the prompts and sample variables above.
                </div>
              </div>
              <div className="input-control separate-label">
                <StreamedOutputReadonly
                  ref={streamedOutputRef}
                  initialHTML=""
                  onStreaming={onPlaygroundStreamingChange}
                  onGenerationError={onPlaygroundGenerationError}
                  contentTitle={`${record.category ?? ''} / ${record.name ?? ''}`}
                  generateContentFn={generatePromptTemplatePlayground}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptTemplatePlayground;
